import React, { useEffect, useState } from "react";
import { getBackendApiUrl } from "../../config/backend";
import { highlightLabel } from "./VehicleTypeahead";
import styles from "./PartialYmmvCard.module.css";
import pickerStyles from "./VehicleTypeahead.module.css";

/**
 * TRAC-896: partial YMMV card.
 *
 * An inline preview slice of the real Find Your Vehicle interface, rendered
 * in the message flow when the agent emits `ymmv_interface` (instead of the
 * old full-screen auto-open). Tapping ANYWHERE on the card opens the existing
 * full-screen YMMV mode via `onOpen` — this component owns zero picker logic.
 *
 * The shape is the search-forward one (was variant B of an A/B, ruled in
 * 8/11): header + a prominent input-STYLED search pill as the centerpiece,
 * one heavily-faded year row beneath. The pill is pure decoration (aria-hidden
 * div, not a focusable input — no keyboard trap, no nested interactive
 * element); the single button is the only tap target.
 *
 * The discarded alternative cropped three year rows instead of the pill, and
 * lost on a hands-on finding worth not re-learning: a stack of rows reads as
 * SCROLLABLE, so people swipe the card and tapping is only their second
 * instinct. The one faded row below the pill survives deliberately — it says
 * "this is a slice of something bigger", which is what makes the morph
 * legible — and one row is too few to read as a list you could scroll.
 */

// The single ghosted row under the search pill. The real interface's
// empty-query browse opens on the newest year, so this is what is actually
// behind the card — purely decorative (the interface fetches live data the
// moment it opens), which is why a static value is honest for a preview.
const PREVIEW_TOP_YEAR = "2027";

/** A previewed option row — the same fields the picker's group rows render from. */
interface PreviewOption {
  label: string;
  variant?: string;
  searchQuery?: string;
}

/**
 * The card's original rule was "never invent options": the list under a
 * prefilled search field only exists after GET /api/ymmv/search runs, so the
 * preview showed two neutral bars. Miles ruled (8/12) that the preview should
 * carry the REAL options instead — so now the fetch runs at card render, and
 * the rows shown are exactly the ones the interface will open with. The rule
 * survives in the fallback: while loading, or if the search fails or returns
 * nothing, the card shows the neutral bars — degraded honesty, never made-up
 * text.
 *
 * Cached per prefill string (as a promise, so several cards with the same
 * vehicle share one request) — which is also what makes it affordable for
 * greyed cards in history to keep showing their rows: the whole transcript
 * costs one request per unique vehicle. Only a COMPLETED card skips the fetch.
 */
const previewCache = new Map<string, Promise<PreviewOption[]>>();

const fetchPreviewOptions = (prefill: string): Promise<PreviewOption[]> => {
  const cached = previewCache.get(prefill);
  if (cached) return cached;
  const p = (async (): Promise<PreviewOption[]> => {
    const res = await fetch(
      `${getBackendApiUrl()}/api/ymmv/search?q=${encodeURIComponent(prefill.trim())}`,
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const groups = (data.groups || []) as {
      label?: string;
      variant?: string;
      searchQuery?: string;
    }[];
    const matches = (data.matches || []) as { display?: string }[];
    const opts: PreviewOption[] = groups.length
      ? groups.map((g) => ({ label: g.label || "", variant: g.variant, searchQuery: g.searchQuery }))
      : matches.map((m) => ({ label: m.display || "" }));
    return opts.filter((o) => o.label);
  })();
  // A failed request must not poison the cache — the next render retries.
  p.catch(() => previewCache.delete(prefill));
  previewCache.set(prefill, p);
  return p;
};

interface PartialYmmvCardProps {
  /**
   * Open the full-screen Find Your Vehicle interface. Receives this card's own
   * button element so the parent can morph the picker out of it (TRAC-896) —
   * the card is the only thing that knows which of several in history was
   * tapped, and it stops being measurable the moment the picker mounts.
   */
  onOpen?: (origin: HTMLElement | null) => void;
  /**
   * The emit's prefill — the vehicle the agent already knows ("2020 Toyota
   * Camry"). Same value that is handed to activateYmmvMode on tap, so the card
   * previews the state the interface will actually open into instead of a
   * hardcoded cold start. Undefined = the customer really is starting fresh.
   */
  prefill?: string;
  /** Turn number for ChatTrackingRoot click/impression attribution. */
  turnNumber?: number;
}

/**
 * No `onOpen` means the card is not in the current turn (MessageRenderer only
 * passes one for the latest message), so it renders as an inert record of what
 * was asked — dimmed, not focusable, no morph. Same rule the other cards in the
 * transcript follow. Once a selection has been completed the parent writes the
 * chosen vehicle back onto this part, so a spent card reads as what the
 * customer answered rather than a prompt they still owe.
 */

const PartialYmmvCard: React.FC<PartialYmmvCardProps> = ({ onOpen, prefill, turnNumber }) => {
  // A prefilled emit is a different step, so it gets a different preview.
  // Verified against the real interface: with prefill it opens on the VEHICLE
  // step (title unchanged) with the search field carrying the prefill and that
  // vehicle's trims listed beneath — NOT on the tire-size step.
  const prefilled = !!prefill?.trim();
  const inert = !onOpen;
  // Miles (8/12): a live prefilled card previews the vehicle's REAL options.
  // null = not loaded (render the neutral bars).
  const [previewOptions, setPreviewOptions] = useState<PreviewOption[] | null>(null);
  useEffect(() => {
    // A card that aged out of its turn still shows the SAME rows it showed
    // live (greyed with the rest of it) — the neutral bars would read as a
    // loading state that never resolves. The per-vehicle cache keeps this to
    // one request per unique prefill across the whole transcript.
    if (!prefill?.trim()) return;
    let alive = true;
    fetchPreviewOptions(prefill)
      .then((opts) => {
        if (alive && opts.length) setPreviewOptions(opts.slice(0, 2));
      })
      .catch(() => {
        /* keep the neutral bars */
      });
    return () => {
      alive = false;
    };
  }, [prefill]);
  // Hand this button up as the morph origin. The button itself is never
  // animated (only a detached overlay is), so it stays a stable click target.
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    onOpen?.(e.currentTarget);
  };
  return (
    <button
      type="button"
      className={`${styles.card}${inert ? ` ${styles.cardInert}` : ""}`}
      onClick={handleClick}
      disabled={inert}
      // A disabled control that still announced "opens vehicle search" would be
      // a promise it cannot keep, so a spent card describes itself instead.
      aria-label={
        inert
          ? prefilled
            ? `Vehicle search, no longer active — ${prefill}`
            : "Vehicle search, no longer active"
          : prefilled
            ? `Opens vehicle search for ${prefill}`
            : "Find your vehicle — opens vehicle search"
      }
      data-track-id="ymmv_preview_card"
      // Was the A/B arm; with one shape left it distinguishes the two previews
      // the card actually has. Safe to change outright — the card first reached
      // an environment today, so there is no history to keep continuity with.
      data-track-option={`open_vehicle_search:${prefilled ? "prefilled" : "cold"}`}
      data-track-turn={turnNumber ?? -1}
    >
      <div className={styles.inner}>
        <div className={styles.title}>Find Your Vehicle</div>
        {prefilled ? (
          <div aria-hidden="true">
            {/* The field carries the vehicle the agent already has, styled as
                entered text rather than a placeholder — that is exactly what the
                real field looks like the moment the interface opens. */}
            <div className={styles.searchPill}>
              <svg
                className={styles.searchIcon}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span className={styles.searchValue}>{prefill}</span>
            </div>
            {/* The vehicle's real options (fetched above), cropped and faded the
                way the cold card crops its year row. Until they arrive — or if
                the search fails or returns none — the neutral bars stand in:
                "a list belongs here" without asserting what is in it. Rows are
                never invented; they are the search's actual answer. */}
            {previewOptions ? (
              /* The picker's own row classes and highlight treatment — shared,
                 not imitated, so the preview cannot drift from what the tap
                 opens. Matched tokens stay white, the remainder dims, variant
                 rows keep their chip — exactly as the first-open list renders. */
              <div className={styles.rowsPreview}>
                {previewOptions.map((opt, i) => (
                  <div key={i} className={pickerStyles.resultItem}>
                    {opt.variant && opt.searchQuery ? (
                      <span className={pickerStyles.variantPill}>
                        <span className={pickerStyles.resultLabel}>
                          {highlightLabel(opt.searchQuery, prefill || "")}
                        </span>
                        <span className={pickerStyles.variantChip}>{opt.variant}</span>
                      </span>
                    ) : (
                      <span className={pickerStyles.resultLabel}>
                        {highlightLabel(opt.label, prefill || "")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.optionsGhost}>
                <span className={styles.optionBar} />
                <span className={styles.optionBar} />
              </div>
            )}
          </div>
        ) : (
          <div aria-hidden="true">
            {/* The search box IS the card. Styled to mirror the real typeahead's
                search pill, but a plain div — the button is the only interactive
                element. */}
            <div className={styles.searchPill}>
              <svg
                className={styles.searchIcon}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span className={styles.searchPlaceholder}>Type your year, make, or model…</span>
            </div>
            {/* One heavily-faded year row: a whisper of the list underneath so
                the card still reads as a preview of a larger interface, without
                enough list to invite a swipe. */}
            <div className={styles.rowsGhost}>
              <div className={styles.row}>{PREVIEW_TOP_YEAR}</div>
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

export default PartialYmmvCard;
